import { useEffect, useState, useMemo, useCallback } from "react";
import { AlertCircle, RefreshCw, Filter } from "lucide-react";
import { fetchActivity, type ActivityEntry } from "../api/client";
import { useDealContext } from "../hooks/useDealContext";
import ActivityFeed from "../components/ActivityFeed";
import LoadingSpinner from "../components/LoadingSpinner";

// ---------------------------------------------------------------------------
// Action type filter options
// ---------------------------------------------------------------------------

const ACTION_GROUPS: { label: string; prefix: string }[] = [
  { label: "Documents", prefix: "document_" },
  { label: "Scenarios", prefix: "scenario_" },
  { label: "Tenants", prefix: "tenant_" },
  { label: "Deal", prefix: "deal_" },
  { label: "Access", prefix: "access_" },
  { label: "Comments", prefix: "comment_" },
  { label: "Expenses", prefix: "expense" },
  { label: "Projections", prefix: "projection_" },
  { label: "Assumptions", prefix: "assumptions_" },
];

function matchGroup(action: string): string {
  for (const g of ACTION_GROUPS) {
    if (action.startsWith(g.prefix)) return g.label;
  }
  return "Other";
}

// ---------------------------------------------------------------------------
// Page component
// ---------------------------------------------------------------------------

export default function ActivityPage() {
  const { deal } = useDealContext();
  const [entries, setEntries] = useState<ActivityEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filterGroup, setFilterGroup] = useState<string>("all");
  const [showFilters, setShowFilters] = useState(false);

  const loadActivity = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await fetchActivity(deal.id, 200);
      setEntries(result);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Failed to load activity");
    } finally {
      setLoading(false);
    }
  }, [deal.id]);

  useEffect(() => {
    loadActivity();
  }, [loadActivity]);

  // Derive available groups from data
  const availableGroups = useMemo(() => {
    const groups = new Set<string>();
    for (const e of entries) {
      groups.add(matchGroup(e.action));
    }
    return Array.from(groups).sort();
  }, [entries]);

  // Filter entries
  const filtered = useMemo(() => {
    if (filterGroup === "all") return entries;
    return entries.filter((e) => matchGroup(e.action) === filterGroup);
  }, [entries, filterGroup]);

  return (
    <div className="max-w-[1280px] mx-auto px-4 sm:px-8 py-6">
      {/* Page header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-display text-xl font-semibold text-bark-800">
            Activity
          </h1>
          <p className="text-sm text-bark-400 mt-1">
            All changes and actions on this deal.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowFilters((v) => !v)}
            className={`flex items-center gap-1.5 px-3 py-2 text-sm rounded-lg transition-all duration-200 ${
              showFilters
                ? "bg-forest-100 text-forest-700 font-medium"
                : "text-bark-400 hover:text-bark-700 hover:bg-grove-100"
            }`}
          >
            <Filter className="w-4 h-4" />
            <span className="hidden sm:inline">Filter</span>
          </button>
          <button
            onClick={loadActivity}
            disabled={loading}
            className="flex items-center gap-1.5 px-3 py-2 text-sm text-bark-400 hover:text-bark-700 hover:bg-grove-100 rounded-lg transition-all duration-200 disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            <span className="hidden sm:inline">Refresh</span>
          </button>
        </div>
      </div>

      {/* Filter bar */}
      {showFilters && (
        <div className="mb-4 px-4 py-3 bg-grove-50 border border-grove-200 rounded-xl">
          <div className="flex items-center gap-3">
            <label className="text-[11px] font-medium text-bark-500 whitespace-nowrap">
              Action type
            </label>
            <select
              value={filterGroup}
              onChange={(e) => setFilterGroup(e.target.value)}
              className="px-3 py-1.5 border border-grove-300 rounded-lg text-sm bg-white text-bark-700 focus:outline-none focus:ring-2 focus:ring-forest-500"
            >
              <option value="all">All actions</option>
              {availableGroups.map((g) => (
                <option key={g} value={g}>
                  {g}
                </option>
              ))}
            </select>
            {filterGroup !== "all" && (
              <span className="text-[11px] text-bark-400">
                {filtered.length} of {entries.length} entries
              </span>
            )}
          </div>
        </div>
      )}

      {/* Error state */}
      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-xl flex items-center gap-3 text-red-700 text-sm mb-4">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          {error}
        </div>
      )}

      {/* Loading state */}
      {loading && <LoadingSpinner />}

      {/* Feed */}
      {!loading && !error && (
        <div className="bg-white border border-grove-300 rounded-xl shadow-sm overflow-hidden">
          <div className="px-5 py-3 bg-grove-100 border-b border-grove-300">
            <h3 className="text-[11px] font-semibold uppercase tracking-wider text-bark-500">
              Timeline
              {filtered.length > 0 && (
                <span className="ml-2 text-bark-400 font-normal normal-case">
                  ({filtered.length} event{filtered.length !== 1 ? "s" : ""})
                </span>
              )}
            </h3>
          </div>
          <ActivityFeed entries={filtered} />
        </div>
      )}
    </div>
  );
}
